import React, { useCallback, useEffect, useState } from 'react';
import { Table, Button, Space, Switch, notification, Flex, Input, Select, Tooltip } from 'antd';
import type { TableProps, PaginationProps } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getDimensionCollection, updateDimension } from 'src/api/api';
import DepartmentModal from './departmentModal';
import { useAppSelector } from 'src/redux/hooks';
import { themeSettings } from 'src/configs/index.tsx';

const currentPageInitialState: API.CurrentPageType = {
  number: 1,
  size: 10,
};

const dimensionInitialState: API.DimensionCollection = {
  items: [],
  total: 0,
  page: 1,
  size: 10,
  pages: 0,
}

const Departments: React.FC = () => {
  const { t } = useTranslation();
  const accessToken = useAppSelector(state => state.auth.accessToken);
  const clientId = useAppSelector(state => state.client.selectedClientData.id);
  const [dimensions, setDimensions] = useState<API.DimensionCollection>(dimensionInitialState);
  const [loading, setLoading] = useState(false);
  const [updatingDimension, setUpdatingDimension] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dimensionToEdit, setDimensionToEdit] = useState<API.DimensionItem | null>(null);
  const [currentPage, setCurrentPage] = useState<API.CurrentPageType>(
    currentPageInitialState
  );
  const [searchString, setSearchString] = useState<string>('');
  const [orderType, setOrderType] = useState<'ASC' | 'DESC'>('ASC');


  const fetchDimensions = async () => {
    setLoading(true);

    try {

      const indexOfLastItem = currentPage.number * currentPage.size;
      const indexOfFirstItem = indexOfLastItem - currentPage.size;

      const params: API.DimensionParams = {
        client_id: clientId,
        limit: currentPage.size,
        offset: indexOfFirstItem,
        search: searchString,
        order_type: orderType,
      }

      const result = await getDimensionCollection(accessToken, params);
      setDimensions(result.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (dimension?: API.DimensionItem) => {
    setDimensionToEdit(dimension || null);  // Null if creating a new dimension
    setIsModalOpen(true);
  };

  const switchDimensionStatus = async (record: API.DimensionItem) => {
    setUpdatingDimension(record.id!);
    try {
      await updateDimension(accessToken, { ...record, active: !record.active })
      const msg = record.active
        ? `${record.description} ${' '} ${t('is inactivated.')}`
        : `${record.description} ${' '} ${t('is activated.')}`;

      notification['success']({
        message: t('Success'),
        description: msg,
        placement: 'topRight',
        duration: 4.5,
      });

      setDimensions((prevDimensions: API.DimensionCollection) => {
        const dimensionItems = prevDimensions.items.map((dim) => {
          if (dim.id === record.id) {
            return {
              ...dim,
              active: !record.active,
            };
          }
          return dim;
        });
        return {
          ...prevDimensions,
          items: [
            ...dimensionItems,
          ],
        };
      });

    }
    catch (error) {
      notification['error']({
        message: t('Error'),
        description: t('Could not change status of department'),
        placement: 'topRight',
        duration: 4.5,
      });
      console.error(error);
    }
    finally {
      setUpdatingDimension('');
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false);
    setDimensionToEdit(null);
  };

  useEffect(() => {
    fetchDimensions();
  }, [accessToken, clientId, currentPage]);

  const onPageChange: PaginationProps['onChange'] = (page, pageSize) => {
    setCurrentPage({
      number: page,
      size: pageSize,
    });
  };

  const onShowSizeChange: PaginationProps['onShowSizeChange'] = (
    current,
    pageSize
  ) => {
    setCurrentPage({
      number: current,
      size: pageSize,
    });
  };


  const columns: TableProps<API.DimensionItem>['columns']  = [
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('Code'),
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: t('Active'),
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean, record: API.DimensionItem) => (
        <Switch
          key={record.id + 'isActive'}
          style={{ backgroundColor: active ? '#52c41a' : 'orange' }}
          checkedChildren={t('Activated')}
          unCheckedChildren={t('Inactivated')}
          value={record.active}
          loading={record.id === updatingDimension}
          onChange={() => switchDimensionStatus(record)}
        />
      ),
    },
    {
      title: t('Action'),
      key: 'action',
      render: (_: any, record: API.DimensionItem) => (
        <Button type="primary" onClick={() => openModal(record)}>
          {t('Edit')}
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      
      <Button type="primary" onClick={() => openModal()}>
        {`${t('Add')} ${t('Department').toLowerCase()} `}
      </Button>

      <Flex wrap gap="small" justify='space-between' >
        <Space>
          <Input
            placeholder={t('Search Code/Description')}
            style={{ width: 300 }}
            onChange={(event) => {
              setSearchString(event.target.value);
            }}
            allowClear
          />
          <Select
            defaultValue="ASC"
            style={{ width: 120 }}
            onChange={(value: 'ASC' | 'DESC') => setOrderType(value)}
            options={[
              { value: 'ASC', label: 'ASC' },
              { value: 'DESC', label: 'DESC' },
            ]}
          />
          <Button type="primary" onClick={() => onPageChange(1, currentPage.size)}>
            {t('Filter Departments')}
          </Button>
        </Space>

        <Tooltip title={t('Refresh')} color={themeSettings.token.colorPrimary} key="refresh-company-cards" >
          <RedoOutlined onClick={fetchDimensions} />
        </Tooltip>
      </Flex>

      <Table
        columns={columns}
        dataSource={dimensions?.items}
        loading={loading}
        rowKey={'id'}
        pagination={{
          defaultCurrent: currentPageInitialState.number,
          current: currentPage.number,
          onChange: onPageChange,
          defaultPageSize: currentPageInitialState.size,
          pageSize: currentPage.size,
          onShowSizeChange: onShowSizeChange,
          total: dimensions?.total ?? 0,
          showSizeChanger: true,
        }}
      />

      <DepartmentModal
        open={isModalOpen}
        onClose={handleModalClose}
        dimension={dimensionToEdit}
        onSubmit={fetchDimensions}  // Refetch dimensions on submit
      />

    </Space>
  );
};

export default Departments;
